// components/BulkPlayerUpload.jsx
import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { showToast } from "./ToastNotification";
import { FaTimes } from "react-icons/fa";
import { Download, UploadCloud } from "lucide-react";

const BulkPlayerUpload = ({
  team,
  selectedSubcategory,
  onClose,
  onUploadSuccess,
}) => {
  const token = useSelector((state) => state.user.token);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const popupRef = useRef(null);
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        selectedFile.type === "application/vnd.ms-excel")
    ) {
      setFile(selectedFile);
      setError("");
    } else {
      setError("Por favor, selecciona un archivo de Excel (.xlsx o .xls).");
      setFile(null);
    }
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        first_name: "Lionel",
        last_name: "Messi",
        identification: "",
        birth_date: "1987-06-24",
        phone: "0991234567",
        city: "Rosario",
        player_type: "Profesional",
        review: "Capitán",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Jugadores");
    XLSX.writeFile(workbook, "plantilla_jugadores.xlsx");
  };

  const handleSubmit = () => {
    if (!file) {
      setError("Por favor, selecciona un archivo para subir.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("El archivo es demasiado grande. Máx: 5MB.");
      return;
    }

    setLoading(true);
    setError("");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonOptions = {
          raw: false,
          dateNF: "yyyy-mm-dd",
        };
        const playersData = XLSX.utils.sheet_to_json(worksheet, jsonOptions);

        if (!playersData.length) {
          throw new Error(
            "El archivo está vacío o no tiene el formato correcto."
          );
        }

        // Validación de cada fila
        const errors = [];
        const validPlayers = playersData.map((player, index) => {
          const rowNum = index + 2; // +2 porque Excel tiene encabezado
          if (!player.first_name || !player.last_name) {
            errors.push(`Fila ${rowNum}: Nombre y apellido son obligatorios.`);
          }
          if (!player.birth_date || isNaN(new Date(player.birth_date))) {
            errors.push(`Fila ${rowNum}: Fecha de nacimiento inválida.`);
          }
          if (!player.city) {
            errors.push(`Fila ${rowNum}: Ciudad es obligatoria.`);
          }
          if (!player.player_type) {
            errors.push(`Fila ${rowNum}: Tipo de jugador es obligatorio.`);
          }
          // Opcionales: identification y phone
          if (player.identification && player.identification.length > 20) {
            errors.push(`Fila ${rowNum}: Identificación demasiado larga.`);
          }
          if (player.phone && !/^\d{7,15}$/.test(player.phone)) {
            errors.push(`Fila ${rowNum}: Teléfono inválido.`);
          }

          return player;
        });

        if (errors.length) {
          throw new Error(errors.join("\n"));
        }

        // Enviar al backend solo si no hay errores
        const response = await fetch(
          "https://server-postgre.vercel.app/api/players/bulk-create",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              team_id: team.id,
              team_category_id: selectedSubcategory?.id || null,
              players: validPlayers,
            }),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.message || "Error en el servidor.");
        }

        showToast.success(
          `${result.count} jugadores creados y asignados exitosamente.`
        );
        onUploadSuccess();
      } catch (err) {
        setError(err.message);
        showToast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]">
      <div
        ref={popupRef}
        className="bg-white rounded-2xl shadow-xl w-11/12 max-w-lg p-6 relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
        >
          <FaTimes size={20} />
        </button>
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          Carga Masiva de Jugadores
        </h2>
        <p className="text-center text-gray-600 mb-4">
          Sube un archivo Excel con la lista de jugadores.
        </p>

        <div className="text-center mb-4">
          <button
            onClick={handleDownloadTemplate}
            className="text-blue-500 hover:underline font-semibold flex items-center justify-center gap-2 w-full"
          >
            <Download size={18} /> Descargar Plantilla
          </button>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
          <label
            htmlFor="file-upload"
            className="cursor-pointer text-blue-600 font-semibold hover:text-blue-800"
          >
            <span>Selecciona un archivo</span>
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              className="sr-only"
              onChange={handleFileChange}
              accept=".xlsx, .xls"
            />
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {file ? `Archivo: ${file.name}` : "o arrástralo aquí"}
          </p>
        </div>

        {error && <p className="text-red-500 text-center mt-4">{error}</p>}

        <div className="mt-6 flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={loading || !file}
            className="bg-green-500 text-white px-6 py-2 rounded-xl font-semibold hover:bg-green-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Procesando..." : "Subir Jugadores"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkPlayerUpload;
